import React, { useState, useEffect, useRef } from "react";
import { Layers, Navigation, RefreshCw, Map } from "lucide-react";
import { Button } from "../../components/AdminSharedComponents";
import { BASE_LAYERS } from "./utils";

// ===============================
// CONSTANTS
// ===============================
const CONTROL_BUTTONS = [
  {
    id: "sidebar",
    icon: Layers,
    onClick: (showSidebar, setShowSidebar) => setShowSidebar(!showSidebar),
    tooltip: (showSidebar) =>
      `${showSidebar ? "Hide" : "Show"} excavation list`,
    className: "hidden lg:flex",
  },
  {
    id: "reset",
    icon: Navigation,
    onClick: (showSidebar, setShowSidebar, resetMapView) => resetMapView(),
    tooltip: () => "Reset map view",
    className: "",
  },
  {
    id: "refresh",
    icon: RefreshCw,
    onClick: () => window.location.reload(),
    tooltip: () => "Refresh data",
    className: "",
  },
];

// ===============================
// COMPONENT
// ===============================
const MapControls = ({
  showSidebar,
  setShowSidebar,
  resetMapView,
  hoveredButton,
  setHoveredButton,
  selectedBaseLayer,
  onLayerChange,
}) => {
  const [layerDropdownOpen, setLayerDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setLayerDropdownOpen(false);
      }
    };

    if (layerDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [layerDropdownOpen]);

  return (
    <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
      {/* Click outside overlay to close dropdown */}
      {layerDropdownOpen && (
        <div
          className="fixed inset-0 z-[999]"
          onClick={() => setLayerDropdownOpen(false)}
        />
      )}

      {/* Layer Selection Dropdown */}
      <div
        ref={dropdownRef}
        className="relative map-control-button"
        onMouseEnter={() => setHoveredButton("layers")}
        onMouseLeave={() => setHoveredButton(null)}
      >
        <Button
          variant="primary"
          size="md"
          onClick={() => setLayerDropdownOpen(!layerDropdownOpen)}
          className="shadow-lg"
        >
          <Map className="h-5 w-5" />
        </Button>
        {hoveredButton === "layers" && (
          <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 whitespace-nowrap bg-primary text-white text-xs px-2 py-1 rounded shadow">
            Map Layers
          </div>
        )}

        {layerDropdownOpen && (
          <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-[1001] max-h-80 overflow-y-auto sm:w-52 md:w-56 lg:w-56 xl:w-60">
            <div className="py-2">
              <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 mb-1">
                Map Style
              </div>
              {BASE_LAYERS.map((layer) => (
                <button
                  key={layer.name}
                  className={`flex w-full items-center px-3 py-2.5 hover:bg-gray-50 transition-colors duration-150 ${
                    selectedBaseLayer.name === layer.name
                      ? "bg-primary/5 text-primary border-r-2 border-primary"
                      : "text-gray-700"
                  }`}
                  onClick={() => {
                    onLayerChange(layer.name);
                    setLayerDropdownOpen(false);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full border-2 transition-colors"
                      style={{
                        backgroundColor:
                          selectedBaseLayer.name === layer.name
                            ? "#3b82f6"
                            : "transparent",
                        borderColor:
                          selectedBaseLayer.name === layer.name
                            ? "#3b82f6"
                            : "#d1d5db",
                      }}
                    />
                    <span className="text-sm font-medium">{layer.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Other Control Buttons */}
      {CONTROL_BUTTONS.map(
        ({ id, icon: Icon, onClick, tooltip, className }) => (
          <div
            key={id}
            className="relative map-control-button"
            onMouseEnter={() => setHoveredButton(id)}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <Button
              variant="primary"
              size="md"
              onClick={() => onClick(showSidebar, setShowSidebar, resetMapView)}
              className={`${className} shadow-lg`}
            >
              <Icon className="h-5 w-5" />
            </Button>
            {hoveredButton === id && (
              <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 whitespace-nowrap bg-primary text-white text-xs px-2 py-1 rounded shadow">
                {tooltip(showSidebar)}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default MapControls;
